import styled from 'styled-components'

import { useToggle } from '@scouts/helpers'
import { ContactWidget, InlineLink } from '@scouts/ui'

import { SUPPORT_PHONE_PRIORITY, SUPPORT_PHONE_PRIORITY_COMPANIES } from '@/constants'
import { navigateToWhatsApp } from '@/helpers'

import { useIntercomActions } from '@/hooks/use-intercom-actions'

import supportImage from '@/assets/images/default-support.png'

import { PhoneCallRequestModal } from './phone-support/PhoneCallRequestModal'

interface SupportWidgetProps {
    withPhoneCallRequest?: boolean
    withPhoneNumber?: boolean
    withCompanyPhoneNumber?: boolean
    withAction?: boolean
    withWhatsApp?: boolean
}

/**
 * Opening "tel:" links in a new tab helps Android users pre-fill the phone number in the dialer.
 */
const TelephoneLink = styled.a.attrs({ target: '_blank' })``

export const SupportWidget = ({
    withWhatsApp = false,
    withPhoneCallRequest = false,
    withAction = true,
    withPhoneNumber = false,
    withCompanyPhoneNumber = false,
}: SupportWidgetProps) => {
    const { openIntercom } = useIntercomActions()
    const [isModalOpen, openPhoneCallRequestModal, closePhoneCallRequestModal] = useToggle()

    const action = withPhoneCallRequest ? openPhoneCallRequestModal : openIntercom
    const actionLabel = withPhoneCallRequest ? 'Llámame' : 'Abrir chat'

    const supportPhoneNumber = withCompanyPhoneNumber ? SUPPORT_PHONE_PRIORITY_COMPANIES : SUPPORT_PHONE_PRIORITY

    return (
        <>
            <ContactWidget
                imageSrc={supportImage}
                action={withAction ? action : undefined}
                actionLabel={withAction ? actionLabel : undefined}
            >
                {withWhatsApp && (
                    <ContactWidget.Line>
                        Si tienes alguna pregunta, no te cortes.{' '}
                        <InlineLink onClick={navigateToWhatsApp}>Hazla a través de WhatsApp</InlineLink> o del chat, que
                        para eso estamos.
                    </ContactWidget.Line>
                )}

                {withPhoneCallRequest && (
                    <ContactWidget.Line>
                        ¿Alguna duda? Estamos aquí para ayudarte, escríbenos en el{' '}
                        <InlineLink onClick={openIntercom}>chat</InlineLink> o&nbsp;llámanos&nbsp;al&nbsp;{' '}
                        <InlineLink as={TelephoneLink} href={`tel:${supportPhoneNumber}`}>
                            <NoWrap>{supportPhoneNumber}</NoWrap>
                        </InlineLink>
                    </ContactWidget.Line>
                )}

                {!withPhoneCallRequest && !withWhatsApp && (
                    <ContactWidget.Line>
                        {withPhoneNumber ? (
                            <>
                                ¿Alguna duda? Estamos aquí para ayudarte, escríbenos en el{' '}
                                <InlineLink onClick={openIntercom}>chat</InlineLink> o llámanos al{' '}
                                <InlineLink as={TelephoneLink} href={`tel:${SUPPORT_PHONE_PRIORITY}`}>
                                    <NoWrap>{SUPPORT_PHONE_PRIORITY}</NoWrap>
                                </InlineLink>
                            </>
                        ) : (
                            <>
                                ¿Tienes alguna duda? Nuestro equipo de soporte está{' '}
                                <InlineLink onClick={openIntercom}>aquí</InlineLink> para ayudarte.
                            </>
                        )}
                    </ContactWidget.Line>
                )}
            </ContactWidget>

            {isModalOpen && <PhoneCallRequestModal onClose={closePhoneCallRequestModal} />}
        </>
    )
}

const NoWrap = styled.span`
    white-space: nowrap;
`
